// 月历：照 iOS 日历 + 提醒事项的结构——左边小月历和「日历」类型筛选，中间是日 / 周 / 月视图，右边是按优先级分组的待办。
// 日程只有日期没有钟点，所以中间不做时间轴；日视图是当天的清单。
// 颜色只表示类型（任务墨、项目节点紫、客户交付橙、审批截止绿、OKR 青）；优先级用全中台同一套图标和勾选圆圈。
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router";
import { taskDate, taskOverdue, dueLabel } from "../tasks/time";
import { dateOnly, priorityLabels, useTasks, useWorkDirectory } from "../tasks/useTasks";
import {
  calendarDays,
  calendarKinds,
  useCalendar,
  useMobileCalendar,
  mobileCalendarDays,
  calendarDayLabel,
} from "./useCalendar";
import { PriorityIcon, TaskCheck, type TaskStatus } from "../../ui/Priority";
import type { CalendarItem, Task } from "../../api/hub/types";
import { ArrowUpRight, CalendarClock, CalendarDays, UserRound, X } from "lucide-react";
import "../../ui/sheet.css";
import { TaskFlags, ownerLabel } from "../tasks/TaskMeta";
import "./calendar.css";
import { useWorkspaceView, useWorkspaceRecords } from '../workspace/useWorkspace';
import { WorkspaceViewControls, PageAssistant, OperationStatus } from '../workspace/WorkspaceControls';

const WEEKDAYS = ["一", "二", "三", "四", "五", "六", "日"];
/** 待办分组：紧急 / 高 / 中 / 低·无 */
const GROUPS: { title: string; icon: number; match: (p: number) => boolean }[] = [
  { title: "紧急", icon: 1, match: (p) => p === 1 },
  { title: "高", icon: 2, match: (p) => p === 2 },
  { title: "中", icon: 3, match: (p) => p === 3 },
  { title: "低 · 无", icon: 0, match: (p) => p === 4 || p === 0 },
];

export function CalendarPage() {
  const [selectedDate, setDate] = useState<Date | null>(null),
    [expanded, setExpanded] = useState<string[]>([]),
    [active, setActive] = useState<CalendarItem | null>(null),
    // 点开的条目在屏幕上的位置：详情卡就从那里弹出来（窄屏仍从底部升起）
    [anchor, setAnchor] = useState<DOMRect | null>(null),
    [newDate, setNewDate] = useState(""),
    [error, setError] = useState("");
  const view = useWorkspaceView('calendar');
  // 默认看团队：每个人这一周每天要做什么（2026-10-21 用户要的是团队维度，不是个人日程）
  const mode = String(view.draft.display.mode ?? 'person');
  const setMode = (value: string) => view.patch({ display: { ...view.draft.display, mode: value } });
  // 团队默认按月看（2026-10-21 用户要求），也可以切回按周
  const teamRange = String(view.draft.display.teamRange ?? "month");
  const setTeamRange = (value: string) => view.patch({ display: { ...view.draft.display, teamRange: value } });
  const showEmpty = view.draft.display.showEmpty === true;
  const setShowEmpty = (value: boolean) => view.patch({ display: { ...view.draft.display, showEmpty: value } });
  const person = String(view.draft.filters.assigneeId ?? '');
  const kinds = Array.isArray(view.draft.filters.kind) ? view.draft.filters.kind as string[] : typeof view.draft.filters.kind === 'string' ? [view.draft.filters.kind] : Object.keys(calendarKinds);
  const setPerson = (value: string) => { const filters = { ...view.draft.filters }; if (value) filters.assigneeId = value; else delete filters.assigneeId; view.patch({ filters }); };
  const setKinds = (update: (previous: string[]) => string[]) => view.patch({ filters: { ...view.draft.filters, kind: update(kinds) } });
  const deviceMobile = useMobileCalendar();
  const mobile = deviceMobile || view.draft.layout === 'list';
  useEffect(() => { const from = view.baseline?.filters.from; if (typeof from === 'string') setDate(new Date(`${from}T12:00:00`)); }, [view.activeViewId, view.loadedVersion]);
  useEffect(() => {
    if (!active) return;
    const key = (e: KeyboardEvent) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [active]);
  const directory = useWorkDirectory(),
    timeZone = String(directory.settings.data?.timeZone || "Asia/Shanghai"),
    today = taskDate(Date.now(), timeZone),
    date = selectedDate ?? new Date(`${today}T12:00:00`),
    // 团队按月：一页的月格子，每天里是全组的事，条目前标是谁的；团队按周才是「一行一个人」的表
    teamMonth = mode === "person" && teamRange === "month",
    grid = teamMonth ? "month" : mode,
    wholeMonth = grid === "month",
    days = grid === "day" ? [dateOnly(date)] : calendarDays(date, grid === "person" ? "week" : grid, view.draft.display.weekStartsOn === 0 ? 0 : 1),
    work = useCalendar(wholeMonth ? dateOnly(new Date(date.getFullYear(), date.getMonth(), 1)) : days[0], wholeMonth ? dateOnly(new Date(date.getFullYear(), date.getMonth() + 1, 0)) : days.at(-1)!, person, { viewId: view.activeViewId || undefined, filters: view.draft.filters, sorts: view.draft.sorts, layout: view.draft.layout, groupBy: view.draft.groupBy, timeZone }),
    all = (work.items.data ?? []).map((i) => ({ ...i, date: taskDate(i.date, timeZone) })),
    items = all;
  const displayedDays = mobile && grid !== "day" ? mobileCalendarDays(days, items, date.getMonth(), grid, showEmpty) : days;
  const monthDays = calendarDays(date, "month", view.draft.display.weekStartsOn === 0 ? 0 : 1);
  const weekdays = view.draft.display.weekStartsOn === 0 ? [WEEKDAYS[6], ...WEEKDAYS.slice(0, 6)] : WEEKDAYS;
  const shift = (n: number) =>
    setDate(
      grid === "month"
        ? new Date(date.getFullYear(), date.getMonth() + n, 1)
        : new Date(date.getFullYear(), date.getMonth(), date.getDate() + n * (["week", "person"].includes(mode) ? 7 : 1)),
    );
  const move = (id: string, target: string) => {
    const item = active?.id === id ? active : all.find(i => i.id === id);
    const capability = item?.capabilities?.reschedule;
    if (!capability?.allowed || capability.expectedVersion === undefined) { setError(capability?.reason ?? '此来源当前只读'); return; }
    work.move.mutate(
      { id, date: target, expectedVersion: capability.expectedVersion, timeZone },
      { onError: (e) => setError((e as { status?: number }).status === 409 ? `${e.message}。改期草稿已保留，请重读最新日程。` : e.message), onSuccess: () => { setActive(null); setError(''); } },
    );
  };
  // 团队按月的条目前标是谁的（头像字），悬停看全名
  const whoName = (i: CalendarItem) => directory.people.data?.find((p) => p.id === i.personId)?.name ?? "未指定";
  const event = (i: CalendarItem) => (
    <button
      key={i.id}
      draggable={i.capabilities?.reschedule?.allowed === true}
      onDragStart={(e) => { if (i.capabilities?.reschedule?.allowed) e.dataTransfer.setData("text/calendar-item", i.id); else e.preventDefault(); }}
      onClick={(e) => {
        setAnchor(e.currentTarget.getBoundingClientRect());
        setActive(i);
        setNewDate(i.date.slice(0, 10));
      }}
      className="calendar-event"
      data-kind={i.kind}
      data-late={i.kind === "task" && i.date < today ? true : undefined}
      title={`${teamMonth ? `${whoName(i)} · ` : ""}${calendarKinds[i.kind]}：${i.title}${i.capabilities?.reschedule?.allowed ? '' : ` · ${i.capabilities?.reschedule?.reason ?? '只读'}`}`}
    >
      {teamMonth && <i className="calendar-event-who" aria-hidden="true">{whoName(i).slice(0, 1)}</i>}
      {!teamMonth && i.kind === "task" && i.priority > 0 && <PriorityIcon priority={i.priority} label={false} />}
      <span className="calendar-event-title">{i.title}</span>
      {i.assigneeType === "agent" && <small> · {ownerLabel({ assigneeId: i.personId ?? null, assigneeType: i.assigneeType }, directory.people.data ?? [])}</small>}
      <TaskFlags task={i} />
    </button>
  );

  const renderEvents = (entries: CalendarItem[]) => {
    const groupBy = view.draft.groupBy;
    if (!groupBy) return entries.map(event);
    const key = (i: CalendarItem) => groupBy === 'kind' ? calendarKinds[i.kind] : groupBy === 'assigneeId' ? directory.people.data?.find(p => p.id === i.personId)?.name ?? '未指定' : groupBy === 'projectId' ? directory.projects.data?.find(p => p.id === i.projectId)?.title ?? '未归项目' : priorityLabels[i.priority];
    return [...new Set(entries.map(key))].map(group => <div key={group} className="calendar-event-group"><h3>{group}</h3>{entries.filter(i => key(i) === group).map(event)}</div>);
  };

  // 详情卡贴着点开的条目：优先放在右边，放不下放左边；上下不出屏。窄屏（底部升起）不定位
  const sheetRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = sheetRef.current;
    if (!el || !anchor) return;
    if (window.innerWidth <= 600) { el.style.removeProperty("left"); el.style.removeProperty("top"); return; }
    const gap = 8, w = el.offsetWidth, h = el.offsetHeight, vw = window.innerWidth, vh = window.innerHeight;
    const right = anchor.right + gap, left = anchor.left - gap - w;
    const x = right + w <= vw - gap ? right : left >= gap ? left : Math.max(gap, Math.min(anchor.left, vw - w - gap));
    const y = Math.max(gap, Math.min(anchor.top - gap, vh - h - gap));
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.transformOrigin = x >= anchor.right ? "left top" : "right top";
  }, [active, anchor]);
  // 窄屏横向滑动时，先把「今天」那一列滑到名字旁边
  const teamRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // 宽屏滑到本周一（今天之前这几天的逾期也看得到）
    const t = new Date(`${today}T12:00:00`), monday = dateOnly(new Date(t.getFullYear(), t.getMonth(), t.getDate() - ((t.getDay() + 6) % 7)));
    // 窄屏放不下一周，就从今天开始
    const box = teamRef.current, start = box && box.clientWidth < 700 ? today : monday;
    const cell = box?.querySelector<HTMLElement>(`.calendar-team-day[data-day="${start}"]`) ?? box?.querySelector<HTMLElement>('.calendar-team-day'), name = box?.querySelector<HTMLElement>('.calendar-team-name');
    if (box && cell && name && box.scrollWidth > box.clientWidth) box.scrollLeft += cell.getBoundingClientRect().left - name.getBoundingClientRect().right;
  }, [mode, days[0], work.items.isPending]);
  // ———— 团队：一行一个人，一列一天；单元格里是这个人当天的事（含本人的 AI），可以拖到别的日子改期 ————
  const teamPeople = (directory.people.data ?? []).filter((p) => !person || p.id === person);
  const teamRows = [
    ...teamPeople.map((p) => ({ id: p.id, name: p.name, entries: items.filter((i) => i.personId === p.id) })),
    ...(!person && items.some((i) => !teamPeople.some((p) => p.id === i.personId))
      ? [{ id: "", name: "未指定", entries: items.filter((i) => !teamPeople.some((p) => p.id === i.personId)) }]
      : []),
  ];
  const teamGrid = (
    <div className="calendar-team" role="table" aria-label="团队日程" ref={teamRef} style={{ "--team-days": days.length } as CSSProperties}>
      <div className="calendar-team-row calendar-team-head" role="row">
        <span className="calendar-team-name" role="columnheader">成员</span>
        {days.map((day, n) => (
          <span key={day} role="columnheader" className="calendar-team-day" data-day={day} data-today={day === today} data-weekend={[0, 6].includes(new Date(`${day}T12:00:00`).getDay()) || undefined}>
            <small>{weekdays[n]}</small>
            <b>{Number(day.slice(8))}</b>
          </span>
        ))}
      </div>
      {teamRows.map((r) => {
        const late = r.entries.filter((i) => i.kind === "task" && i.date < today).length;
        return (
          <div key={r.id || "none"} className="calendar-team-row" role="row" aria-label={r.name} data-empty={!r.entries.length || undefined}>
            <span className="calendar-team-name" role="rowheader">
              <i className="calendar-team-avatar" aria-hidden="true">{r.name.slice(0, 1)}</i>
              <b>{r.name}</b>
              <small>{r.entries.length ? `${r.entries.length} 件` : "这周空"}{late > 0 && <em> · {late} 逾期</em>}</small>
            </span>
            {days.map((day) => {
              const entries = r.entries.filter((i) => i.date.slice(0, 10) === day),
                key = `${r.id}:${day}`,
                showAll = expanded.includes(key),
                limit = 3;
              return (
                <div
                  key={day}
                  role="cell"
                  className="calendar-team-cell"
                  data-today={day === today}
                  data-weekend={[0, 6].includes(new Date(`${day}T12:00:00`).getDay()) || undefined}
                  aria-label={`${r.name} ${calendarDayLabel(day)}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData("text/calendar-item");
                    if (id) move(id, day);
                  }}
                >
                  {entries.slice(0, showAll ? undefined : limit).map(event)}
                  {entries.length > limit && (
                    <button
                      className="calendar-more"
                      aria-label={`${r.name} ${day} ${showAll ? "收起" : "更多事件"}`}
                      onClick={() => setExpanded((s) => (showAll ? s.filter((d) => d !== key) : [...s, key]))}
                    >
                      {showAll ? "收起" : `+${entries.length - limit}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      {!teamRows.length && <p className="work-empty">还没有团队成员</p>}
    </div>
  );

  // ———— 右栏：待办（任务按优先级分组；跟着「按人看月历」走） ————
  const tasksApi = useTasks();
  const rangeEnd = grid === "month" ? monthDays.at(-1)! : days.at(-1)!;
  const todoRecords = useWorkspaceRecords<Task>({ source: 'tasks', timeZone, filters: { status: ['todo', 'in_progress', 'in_review', 'done'], to: rangeEnd, ...(person ? { assigneeId: person } : {}) } });
  const todos = todoRecords.data?.items ?? [];
  const doneCount = todos.filter((t) => t.status === "done").length;
  const toggle = (t: Task) =>
    tasksApi.update.mutate(
      { id: t.id, patch: { status: t.status === "done" ? "todo" : "done", expectedUpdatedAt: t.updatedAt, ...(t.version ? { expectedVersion: t.version } : {}) } },
      { onError: (e) => setError(e.message) },
    );

  return (
    <section className="work-page calendar-page" data-layout={view.draft.layout}>
      <header className="work-heading">
        <h1>
          {date.getFullYear()} 年 {date.getMonth() + 1} 月
          {mode === "day" && <span className="calendar-day-title">{calendarDayLabel(dateOnly(date))}</span>}
          {mode === "person" && !teamMonth && <span className="calendar-day-title">{Number(days[0].slice(5, 7))}月{Number(days[0].slice(8))}日 – {Number(days.at(-1)!.slice(5, 7))}月{Number(days.at(-1)!.slice(8))}日</span>}
        </h1>
        <p className="work-stats">
          <span><b>{items.length}</b> 个日程</span>
          <span><b>{todos.length - doneCount}</b> 件待办</span>
          {(() => {
            const late = todos.filter((t) => t.status !== "done" && taskOverdue(t.dueDate, today, timeZone)).length;
            return late > 0 ? <span data-tone="bad"><b>{late}</b> 逾期</span> : null;
          })()}
        </p>
        <div className="work-heading-actions">
        <div className="calendar-nav">
          <div className="work-tabs" role="group" aria-label="视图">
            {[["person", "团队"], ["day", "日"], ["week", "周"], ["month", "月"]].map(([m, t]) => (
              <button key={m} aria-pressed={mode === m} onClick={() => setMode(m)}>{t}</button>
            ))}
          </div>
          {mode === "person" && (
            <div className="work-tabs calendar-team-range" role="group" aria-label="团队范围">
              {[["month", "按月"], ["week", "按周"]].map(([v, t]) => (
                <button key={v} aria-pressed={teamRange === v} onClick={() => setTeamRange(v)}>{t}</button>
              ))}
            </div>
          )}
          <div className="calendar-step">
          <button aria-label="上个月或周" onClick={() => shift(-1)}>←</button>
          <button onClick={() => setDate(null)}>今天</button>
          <button aria-label="下个月或周" onClick={() => shift(1)}>→</button>
          </div>
        </div>
        <span className="work-divider" aria-hidden="true" />
        <WorkspaceViewControls view={view} />
        <PageAssistant context={{ source: 'calendar', viewId: view.activeViewId || undefined, visibleRange: work.range, selectedRecordIds: active ? [active.id] : [], timeZone }} />
        </div>
      </header>
      <OperationStatus receipt={work.receipt} />
      {(error || work.items.error) && (
        <p role="alert" className="work-error">
          {error || work.items.error?.message}
        </p>
      )}
      <div className="calendar-layout" data-mode={grid} data-team={mode === "person" || undefined}>
        {/* ———— 左栏：小月历 + 日历类型 ———— */}
        <aside className="calendar-side">
          <div className="calendar-panel calendar-mini" aria-label="小月历">
            <div className="calendar-mini-weekdays" aria-hidden="true">
              {weekdays.map((d) => <span key={d}>{d}</span>)}
            </div>
            <div className="calendar-mini-grid">
              {monthDays.map((day) => {
                const kindsOfDay = [...new Set(items.filter((i) => i.date.slice(0, 10) === day).map((i) => i.kind))].slice(0, 3);
                return (
                  <button
                    key={day}
                    className="calendar-mini-day"
                    data-today={day === today}
                    data-selected={selectedDate ? day === dateOnly(date) : undefined}
                    data-other={Number(day.slice(5, 7)) !== date.getMonth() + 1}
                    aria-label={`${calendarDayLabel(day)}${kindsOfDay.length ? `，${kindsOfDay.length} 类日程` : ""}`}
                    onClick={() => {
                      setDate(new Date(`${day}T12:00:00`));
                      setMode("day");
                    }}
                  >
                    <span className="calendar-mini-n">{Number(day.slice(8))}</span>
                    <span className="calendar-mini-dots">{kindsOfDay.map((k) => <i key={k} data-kind={k} />)}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="calendar-panel calendar-kinds">
            <h2>日历</h2>
            <label className="calendar-person">
              <span className="sr-only">按人看</span>
              <select aria-label="按人看月历" value={person} onChange={(e) => setPerson(e.target.value)}>
                <option value="">所有人</option>
                {directory.people.data?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
            {Object.entries(calendarKinds).map(([k, t]) => (
              <label key={k} className="calendar-kind" data-kind={k}>
                <input
                  type="checkbox"
                  checked={kinds.includes(k)}
                  disabled={kinds.length === 1 && kinds.includes(k)}
                  onChange={(e) => setKinds((s) => (e.target.checked ? [...s, k] : s.filter((v) => v !== k)))}
                />
                <span className="calendar-kind-name">{t}</span>
                <span className="calendar-kind-n">{all.filter((i) => i.kind === k).length}</span>
              </label>
            ))}
            {mobile && (
              <label className="calendar-show-empty">
                <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} />
                显示空日期
              </label>
            )}
          </div>
        </aside>

        {/* ———— 中间：日 / 周 / 月 ———— */}
        <div className="calendar-panel calendar-main" title="拖动条目可调整日期；任务先保存在中台，再同步飞书">
          {work.items.isPending ? (
            <p role="status" className="work-empty">正在加载月历…</p>
          ) : (
            <>
              {grid !== "day" && grid !== "person" && (
                <div className="calendar-weekdays" aria-hidden="true">
                  {weekdays.map((d) => <span key={d}>{d}</span>)}
                </div>
              )}
              {mobile && grid !== "day" && grid !== "person" && displayedDays.length === 0 && !work.items.isError && (
                <p className="work-empty" role="status">
                  {grid === "month" ? "本月" : "本周"}没有符合筛选的事项
                </p>
              )}
              {grid === "person" ? teamGrid : <div
                className="calendar-grid"
                data-view={grid}
                aria-label={mobile ? "日程列表" : grid === "day" ? "当天日程" : "日历网格"}
              >
                {displayedDays.map((day) => {
                  const entries = items.filter((i) => i.date.slice(0, 10) === day),
                    limit = grid === "day" ? undefined : 3,
                    showAll = expanded.includes(day) || grid === "day";
                  return (
                    <section
                      key={day}
                      className="calendar-day"
                      data-date={day}
                      data-today={day === today}
                      data-other={grid === "month" && Number(day.slice(5, 7)) !== date.getMonth() + 1}
                      data-view={grid}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const id = e.dataTransfer.getData("text/calendar-item");
                        if (id) move(id, day);
                      }}
                    >
                      <h2>
                        {mobile || grid === "day" ? calendarDayLabel(day) : <span className="calendar-day-n">{Number(day.slice(8))}</span>}
                      </h2>
                      {renderEvents(entries.slice(0, showAll ? undefined : limit))}
                      {grid === "day" && entries.length === 0 && <p className="work-empty">这一天没有日程</p>}
                      {limit && entries.length > limit && (
                        <button
                          className="calendar-more"
                          aria-label={`${day} ${showAll ? "收起" : "更多事件"}`}
                          onClick={() => setExpanded((s) => (showAll ? s.filter((d) => d !== day) : [...s, day]))}
                        >
                          {showAll ? "收起" : `+${entries.length - limit}`}
                        </button>
                      )}
                    </section>
                  );
                })}
              </div>}
            </>
          )}
        </div>

        {/* ———— 右栏：待办 ———— */}
        <aside className="calendar-panel calendar-todo" aria-label="待办">
          <header className="calendar-todo-h">
            <h2>待办</h2>
            <span className="calendar-todo-n" title="已完成 / 全部">{doneCount}/{todos.length}</span>
          </header>
          {todos.length === 0 && <p className="work-empty">{grid === "month" ? "这个月" : grid === "day" ? "这一天" : "这一周"}以前没有要做的任务</p>}
          {GROUPS.map((g) => {
            const list = todos
              .filter((t) => g.match(t.priority))
              .sort((a, b) => (a.status === "done" ? 1 : 0) - (b.status === "done" ? 1 : 0) || (a.dueDate ?? "").localeCompare(b.dueDate ?? ""));
            if (!list.length) return null;
            return (
              <section key={g.title} className="calendar-todo-group">
                <h3><PriorityIcon priority={g.icon} label={false} />{g.title}<span>{list.length}</span></h3>
                {list.map((t) => {
                  const d = dueLabel(t.dueDate, today, timeZone);
                  const done = t.status === "done";
                  const name = directory.people.data?.find((p) => p.id === t.assigneeId)?.name;
                  return (
                    <div key={t.id} className="calendar-todo-row" data-done={done}>
                      <TaskCheck priority={t.priority} status={t.status as TaskStatus} title={t.title} onToggle={() => toggle(t)} />
                      <Link className="calendar-todo-title" to={`/tasks?task=${encodeURIComponent(t.id)}`} aria-label={`${t.title} ${t.identifier} · ${priorityLabels[t.priority]}`}>
                        {t.title}
                      </Link>
                      {name && <span className="calendar-todo-av" title={name} aria-hidden="true">{name.slice(0, 1)}</span>}
                      <span className="calendar-todo-meta" aria-hidden="true">
                        <span className="calendar-todo-id">{t.identifier}</span>
                        {d && <span data-tone={done ? "" : d.tone}>{done ? "已完成" : d.text}</span>}
                      </span>
                    </div>
                  );
                })}
              </section>
            );
          })}
        </aside>
      </div>
      {active && (() => {
        const owner = active.assigneeType === "agent" ? ownerLabel({assigneeId: active.personId ?? null, assigneeType: active.assigneeType}, directory.people.data ?? []) : directory.people.data?.find((p) => p.id === active.personId)?.name;
        const day = active.date.slice(0, 10);
        const rel = dueLabel(day, today, timeZone);
        const source =
          active.kind === "task"
            ? `/tasks?task=${active.sourceId}`
            : active.kind === "approval"
              ? `/approvals?approval=${encodeURIComponent(active.sourceId)}`
              : active.kind === "okr"
                ? `/okr?objective=${encodeURIComponent(active.sourceId)}`
                : `/projects?project=${encodeURIComponent(active.projectId ?? active.sourceId)}`;
        return (
          <div className="work-sheet-backdrop" data-anchored={anchor ? true : undefined} onClick={() => setActive(null)}>
            <aside
              ref={sheetRef}
              className="work-sheet calendar-sheet"
              data-kind={active.kind}
              role="dialog"
              aria-modal="true"
              aria-label="日程预览"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="sheet-head">
                <h2><span className="sheet-kind">{calendarKinds[active.kind]}</span></h2>
                <span className="sheet-head-spacer" />
                <button type="button" className="sheet-close" onClick={() => setActive(null)} aria-label="关闭" title="关闭（Esc）">
                  <X aria-hidden="true" />
                </button>
              </header>
              <div className="sheet-body">
                <h3 className="sheet-title">{active.title}</h3>
                <TaskFlags task={active} />
                <div className="sheet-props">
                  <div className="sheet-prop">
                    <span className="sheet-prop-k"><CalendarDays aria-hidden="true" />日期</span>
                    <span className="sheet-prop-v">
                      {calendarDayLabel(day)}
                      {rel && <span className="calendar-sheet-rel" data-tone={rel.tone}>{rel.text}</span>}
                    </span>
                  </div>
                  {active.kind === "task" && active.priority > 0 && (
                    <div className="sheet-prop">
                      <span className="sheet-prop-k"><PriorityIcon priority={active.priority} label={false} />优先级</span>
                      <span className="sheet-prop-v">{priorityLabels[active.priority]}</span>
                    </div>
                  )}
                  <div className="sheet-prop">
                    <span className="sheet-prop-k"><UserRound aria-hidden="true" />负责人</span>
                    <span className="sheet-prop-v">
                      {owner ? <><span className="sheet-avatar" aria-hidden="true">{owner.slice(0, 1)}</span>{owner}</> : <span className="work-meta">未指定</span>}
                    </span>
                  </div>
                  {active.capabilities?.reschedule?.allowed && <label className="sheet-prop">
                    <span className="sheet-prop-k"><CalendarClock aria-hidden="true" />改到</span>
                    <input type="date" aria-label="调整日期" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                  </label>}
                </div>
                <p className="work-meta calendar-sheet-hint">{active.capabilities?.reschedule?.allowed ? '可拖动改期。先在中台保存，外部状态见操作回执。' : active.capabilities?.reschedule?.reason ?? '此来源当前只读'}</p>
                {error.includes('草稿已保留') && <button onClick={async () => { const latest = await work.items.refetch(); const next = latest.data?.find(i => i.id === active.id); if (next) { setActive(next); setNewDate(taskDate(next.date, timeZone)); setError(''); } }}>重读最新日程（放弃改期草稿）</button>}
              </div>
              <footer className="sheet-foot">
                <Link className="sheet-link" to={source}>
                  打开来源 <ArrowUpRight aria-hidden="true" />
                </Link>
                <span className="sheet-head-spacer" />
                {active.capabilities?.reschedule?.allowed && <button
                  type="button"
                  className="sheet-primary"
                  disabled={!newDate || newDate === day || work.move.isPending}
                  onClick={() => move(active.id, newDate)}
                >
                  保存日期
                </button>}
              </footer>
            </aside>
          </div>
        );
      })()}
    </section>
  );
}
